import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Text,
    Spinner,
    Alert,
    AlertIcon,
    VStack,
    Divider,
    Image,
    Flex,
    Badge,
    Container,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    HStack,
    Button,
    Textarea,
    useToast,
} from "@chakra-ui/react";
import Navbar from "../navbar/Navbar";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/axios";
import { parseApiDate } from "../utils/date";

const getStatusText = (status) => {
    const statusMap = {
        0: "Dnevni zadatak",
        7: "Tjedni zadatak",
        30: "Mjesečni zadatak",
        365: "Godišnji zadatak"
    };
    return statusMap[status] || "Unknown";
};

const dateTimeFormat = (date) => {
    const dateTimeFormat = new Intl.DateTimeFormat("hr-HR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Zagreb",
    })
        .format(parseApiDate(date))
        .replace(/\s+/g, "") // Remove spaces
        .replace(/(\d{2})\.(\d{2})\.(\d{4})\.(\d{2}:\d{2})/, "$1.$2.$3 | $4"); // Add | before time

    return dateTimeFormat;
}

const RecurringTicketDetails = () => {
    const { ticketId } = useParams();
    const { isAuthenticated, currLocation } = useContext(AuthContext);
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [comment, setComment] = useState("");
    const hasFetched = useRef(false);
    const toast = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchTicket = async () => {
            try {
                const response = await api.get(`recurring-tickets/${ticketId}/location/${currLocation}`);
                if (!response.data || typeof response.data !== "object") {
                    throw new Error("Invalid or unexpected API response.");
                }
                setTicket(response.data);
                setError(null);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTicket();
    }, [ticketId]);

    const handleCommentAdd = async () => {
        let newComment;
        try {
            const response = await api.post(`/recurring-tickets/${ticket.id}/comment`, null, {
                params: { commentText: comment },
            });
            newComment = response.data;
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to add comment.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setTicket((prevTicket) => ({
            ...prevTicket,
            comments: [
                {
                    commentedAt: new Date().toISOString(),
                    commentedBy: newComment.commentedBy,
                    text: newComment.text
                },
                ...prevTicket.comments
            ]
        }));

        setComment('');

        toast({
            title: "Comment is added",
            description: "The comment has been successfully added.",
            status: "success",
            duration: 3000,
            isClosable: true,
        });
    };

    return (
        <>
            {isAuthenticated && <Navbar />}
            <Container maxW="700px" p={6} mt={isAuthenticated ? "70px" : "0px"}>
                <Box mb={4} display={{ base: "none", md: "block" }}>
                    <Button onClick={() => navigate('/recurring-tickets')} variant="outline" color="#2a4365" _hover={{ bg: "#bee3f8" }}>
                        ← Natrag
                    </Button>
                </Box>
                <Box bg="white" boxShadow="card" borderRadius="lg" p={6} mt={{ base: "50px", md: "0" }}>
                    {loading ? (
                        <Flex justify="center" align="center" height="200px">
                            <Spinner size="xl" />
                        </Flex>
                    ) : error ? (
                        <Alert status="error">
                            <AlertIcon />
                            {error}
                        </Alert>
                    ) : ticket ? (
                        <VStack spacing={5} align="stretch">
                            <Badge colorScheme="blue" fontSize="sm" p={3} borderRadius="8px" alignSelf="center">
                                {getStatusText(ticket.recurrence)}
                            </Badge>
                            <Text fontSize="3xl" fontWeight="bold" textAlign="center">
                                {ticket.name}
                            </Text>
                            
                            <Text fontSize="l" textAlign="left">
                                {ticket.description}
                            </Text>
                            <Divider />
                            {ticket.imageUrl && <Image src={ticket.imageUrl} alt="Slika zadatka" w="100%" borderRadius="10px" />}
                        </VStack>
                    ) : (
                        <Alert status="error">
                            <AlertIcon />
                            Nevalidan zadatak.
                        </Alert>
                    )}
                </Box>

                {!loading && !error && ticket && (
                    <Box mt={6} p={4} bg="white" boxShadow="card" borderRadius="lg">
                        <Tabs isFitted>
                            <TabList>
                                <Tab>Komentari</Tab>
                                <Tab>Povijest</Tab>
                            </TabList>
                            <TabPanels>
                                <TabPanel>
                                    <HStack w="full" mb={4} align="start" spacing={2}>
                                        <Textarea
                                            placeholder="Dodaj komentar..."
                                            value={comment}
                                            onChange={(e) => {
                                                setComment(e.target.value);
                                                e.target.style.height = "auto";
                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                            }}
                                            flex={1} 
                                            resize="none"
                                            minH="40px"
                                            overflow="hidden"
                                        />
                                        <Button colorScheme="blue" onClick={handleCommentAdd} display={{ base: "none", md: "block" }}>Send</Button>
                                    </HStack>
                                    <Button colorScheme="blue" onClick={handleCommentAdd} w="full" display={{ base: "block", md: "none" }} mb={2}>Send</Button>
                                    <VStack align="start" spacing={3}>
                                        {ticket.comments.map((c, index) => (
                                            <Box key={index} p={4} borderWidth={1} borderRadius="lg" w="full">
                                                <HStack justify="space-between" w="full">
                                                    <Text fontWeight="bold">{c.commentedBy}</Text>
                                                    <Text fontSize="sm" color="gray.500">{dateTimeFormat(c.commentedAt)}</Text>
                                                </HStack>
                                                <Text>{c.text}</Text>
                                            </Box>
                                        ))}
                                    </VStack>
                                </TabPanel>
                                <TabPanel>
                                    <VStack align="start" spacing={3}>
                                        {ticket.completionLogs && ticket.completionLogs.length > 0 ? (
                                            ticket.completionLogs.map((h, index) => (
                                                <Box key={index} p={4} borderWidth={1} borderRadius="lg" w="full">
                                                    <Text fontWeight="bold">{dateTimeFormat(h.completedAt)}</Text>
                                                    <Text>Zadatak je obavio {h.completedByName}</Text>
                                                </Box>
                                            ))
                                        ) : (
                                            <Box w="full" textAlign="center">
                                                <Text fontWeight="bold" color="#2b6cb0" mt={4}>Zadatak nije obavljen</Text>
                                            </Box>
                                        )}
                                    </VStack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </Box>
                )}
            </Container>
        </>
    );
};

export default RecurringTicketDetails;